import Konva from 'konva'
import React, { useEffect, useRef, useState, useMemo, forwardRef, useCallback, useContext } from 'react'
import classNames from 'classnames'
import { Box } from '@material-ui/core'
import { Layer, Stage } from 'react-konva'
import { Provider, ReactReduxContext, useSelector } from 'react-redux'
import { clearAssetsSelection } from 'actions/layer'
import { getLayersIndexes, getVisibleLayerIds, getReferenceVideoAsset, getSelectedAsset } from 'selectors'
import { isOverlayHiddenByAnother } from 'helpers/isOverlayHiddenByAnother'
import AssetOverlay from './AssetsLayers/AssetOverlay'
import Catch from '~/components/base/Catch'
import TransitionAssetOverlay from './AssetsLayers/TransitionAssetOverlay'
import { EditingTransformer } from './EditingTransformer'
import {
  PREVIEW_SUMMARY_HEIGHT_INDENT
} from '~/constant'
import { PLAYBACK_STATE } from '~/enums'
import * as Actions from '~/actions'
import { selectIsResizingEditor } from '~/selectors/useInterface'
import { selectTimelineSegmentPlaybackState } from '~/selectors/playback'
import { selectorSplitPlayers } from '~/selectors/preview'
import { setCanvasPlayerBoxBounds } from '~/actions/preview'
import { useAction, useConst } from '~/hooks'
import { useColorProvider } from '../../../hooks/useColorProvider'
import ColorPicker from './ColorPicker'
import { useCanvasContext } from '../lib'
import { SliderTimeContext } from '~/contexts/SliderTimeContext'

// ---

/**
 * @see https://github.com/konvajs/react-konva/issues/311#issuecomment-454411007
 *
 * DO NOT use this component inside of a component with store connections.
 * I.e. don't do this:
 *
 * <code>
 *   const state = useSelector()
 *   <Stage>
 *     {state.some}
 *   </Stage>
 * </code>
 *
 * Instead, it MUST be like this:
 *
 * <code>
 *   function Child() {
*      const state = useSelector()
 *     ...
 *   }
 *
 *   <Stage>
 *     <Child />
 *   </Stage>
 * </code>
 */
const PatchedStage = forwardRef((props, ref) => {
  const { children, ...rest } = props
  const SliderTimeContextValue = useContext(SliderTimeContext)
  return (
    <ReactReduxContext.Consumer>
      {({ store }) => (
        <Stage {...rest} ref={ref}>
          <Provider store={store}>
            <SliderTimeContext.Provider value={SliderTimeContextValue}>
              {children}
            </SliderTimeContext.Provider>
          </Provider>
        </Stage>
      )}
    </ReactReduxContext.Consumer>
  )
})

function PreviewAssetsOverlay({
  width,
  height,
  overlayAssets,
  composedOverlayAssets,
  overlayTransitions,
  videoRefs,
  setCanvasSource,
  boundingRect,
  scale,
}) {
  const stageRef = useRef()
  const editingStageRef = useRef()
  const editingLayerRef = useRef()
  const canvasPlayerRef = useRef()
  const animation = useConst(new Konva.Animation())
  const transitionsRefs = useConst(new Map())

  const { isColorSelectionEnabled } = useColorProvider('assets-overlay')

  const selectedAsset = useSelector(getSelectedAsset)
  const layersIndexes = useSelector(getLayersIndexes)
  const visibleLayersIds = useSelector(getVisibleLayerIds)
  const referenceAsset = useSelector(getReferenceVideoAsset)
  const isResizing = useSelector(selectIsResizingEditor)
  const isSplittedPreviewPlayers = useSelector(selectorSplitPlayers)
  const timelineSegmentPlaybackState = useSelector(selectTimelineSegmentPlaybackState)

  const clearSelection = useAction(clearAssetsSelection)
  const setCanvasPreviewPlayerBoxBounds = useAction(setCanvasPlayerBoxBounds)
  const updatePreviewVideoSize = useAction(Actions.sourceFiles.updatePreviewVideoSize)

  const [ extSource, setExtSource ] = useState({ id: null, pos: null, size: null, rotation: null })
  const [ isTransform, setTransform ] = useState(false)

  const renderAssets = useMemo(
    () => overlayAssets.concat(composedOverlayAssets)
      .filter(({ layerId }) => visibleLayersIds.indexOf(layerId) !== -1)
      .sort((a, b) => layersIndexes.get(b.layerId) - layersIndexes.get(a.layerId)),
    [ overlayAssets, composedOverlayAssets, layersIndexes, visibleLayersIds ]
  )

  const editAssets = useMemo(
    () => overlayAssets.concat(composedOverlayAssets).sort((a, b) => {
      if (a.selected || b.selected) {
        const a1 = a.selected ? a : b
        const a2 = a.selected ? b : a
        return isOverlayHiddenByAnother(
          a1, a2, referenceAsset, boundingRect, scale, layersIndexes
        ) ? -1 : 0
      }
      return layersIndexes.get(b.layerId) - layersIndexes.get(a.layerId)
    }),
    [ overlayAssets, composedOverlayAssets, layersIndexes, referenceAsset, boundingRect, scale ]
  )

  // Cancel selection if clicked out of the overlay on the preview
  const handleStageClick = useCallback(event => {
    if (!event.target?.attrs?.id && selectedAsset && !isColorSelectionEnabled) {
      clearSelection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedAsset, isColorSelectionEnabled ])

  const handleDragging = useCallback((id, pos) => {
    setExtSource(v => ({ ...v, id, pos }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTransforming = useCallback((id, { size, pos, rotation }) => {
    setExtSource({ id, pos, size, rotation })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setTransformStart = useCallback(isTransform => {
    const transformStarted = Boolean(isTransform)
    setTransform(transformStarted)
    if (!transformStarted) {
      setExtSource({ id: null, pos: null, size: null, rotation: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onAssetsChange = useCallback(id => {
    if (transitionsRefs.size) {
      transitionsRefs.forEach(ref => ref?.update(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTransitionRef = useCallback((id, ref) => {
    if (ref) {
      transitionsRefs.set(id, ref)
    } else {
      transitionsRefs.delete(id)
    }

    return () => transitionsRefs.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setExtSource({ id: null, pos: null, size: null, rotation: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedAsset ])

  useEffect(() => {
    updatePreviewVideoSize(boundingRect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ boundingRect ])

  useEffect(() => {
    if (canvasPlayerRef.current) {
      setCanvasPreviewPlayerBoxBounds(canvasPlayerRef.current.getBoundingClientRect())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ canvasPlayerRef.current, isResizing, isSplittedPreviewPlayers, boundingRect ])

  useEffect(() => {
    if (stageRef.current) {
      setCanvasSource(stageRef.current)
    }

    return () => setCanvasSource(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ stageRef.current ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => animation.stop(), [])
  useCanvasContext({ stageRef, editingStageRef })

  // if canvas is invisible to user,
  // it's makes no sense to render anything
  if (width === 0 || height === 0) {
    return null
  }

  return (
    <Catch>
      <Box
        width={boundingRect.width}
        height={boundingRect.height}
        className={classNames('preview-player__canvas-player', {
          collapse: height <= (boundingRect.height + PREVIEW_SUMMARY_HEIGHT_INDENT),
        })}
        ref={canvasPlayerRef}
      >
        <PatchedStage
          key="view-stage"
          onClick={handleStageClick}
          width={boundingRect.width}
          height={boundingRect.height}
          ref={stageRef}
        >
          {renderAssets.map(asset => (
            <AssetOverlay
              key={`asset-overlay-${asset.id}`}
              index={layersIndexes.size - layersIndexes.get(asset.layerId)}
              asset={asset}
              scale={scale}
              boundingRect={boundingRect}
              referenceAsset={referenceAsset}
              extSourcePos={extSource?.id === asset.id ? extSource.pos : undefined}
              extSourceSize={extSource?.id === asset.id ? extSource.size : undefined}
              extSourceRotation={extSource?.id === asset.id ? extSource.rotation : undefined}
              videoRefs={videoRefs}
              animation={animation}
              onChange={onAssetsChange}
              isTransform={isTransform}
            />
          ))}
          {overlayTransitions
            .filter(({ layerId }) => visibleLayersIds.indexOf(layerId) !== -1)
            .map(asset => (
              <TransitionAssetOverlay
                key={`transition-overlay-${asset.id}`}
                index={layersIndexes.size - layersIndexes.get(asset.layerId)}
                asset={asset}
                boundingRect={boundingRect}
                animation={animation}
                onRef={onTransitionRef}
              />
            ))}
          <If condition={isColorSelectionEnabled && selectedAsset}>
            <ColorPicker
              index={layersIndexes.size + 1}
              asset={selectedAsset}
              scale={scale}
              boundingRect={boundingRect}
              referenceAsset={referenceAsset}
              videoRefs={videoRefs}
            />
          </If>
        </PatchedStage>
      </Box>
      <If condition={timelineSegmentPlaybackState !== PLAYBACK_STATE.PLAY
        && !isColorSelectionEnabled}
      >
        <PatchedStage
          key="editing-stage"
          className={classNames('preview-player__canvas-transformer')}
          onClick={handleStageClick}
          width={width}
          height={height}
          offsetY={height <= (boundingRect.height + PREVIEW_SUMMARY_HEIGHT_INDENT)
            ? -3
            : (height - boundingRect.height) / -2}
          offsetX={(width - boundingRect.width) / -2}
          ref={editingStageRef}
        >
          <Layer key="editing-layer" ref={editingLayerRef} willReadFrequently>
            {/* THE EDITING TRANSFORMERS FOR THE ASSETS */}
            {editAssets.map(asset => (
              <EditingTransformer
                id={asset.id}
                key={`editing-${asset.id}`}
                asset={asset}
                layerRef={editingLayerRef}
                scale={scale}
                boundingRect={boundingRect}
                onDragging={handleDragging}
                onTransforming={handleTransforming}
                onTransformStart={setTransformStart}
                referenceAsset={referenceAsset}
                isTransform={isTransform}
              />
            ))}
          </Layer>
        </PatchedStage>
      </If>
    </Catch>
  )
}

export default PreviewAssetsOverlay
